import { useState, useCallback } from "react";
import { Footer } from "../components/footer/footer";
import { Scroll } from "../components/scroll/scroll";
import { Header } from "../components/header/header";
import { AgentInfo } from "../components/agent-info/agent-info";
import { useI18n } from "../i18n";
import "./password.scss";

const CHARS = {
  lower: "abcdefghijklmnopqrstuvwxyz",
  upper: "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
  numbers: "0123456789",
  special: "!@#$%^&*()-_=+[]{}|;:,.<>?/",
};

function generatePassword(
  length: number,
  useLetters: boolean,
  useNumbers: boolean,
  useSpecial: boolean,
  mixedCase: boolean,
): string {
  let pool = "";
  if (useLetters) {
    pool += mixedCase ? CHARS.lower + CHARS.upper : CHARS.lower;
  }
  if (useNumbers) pool += CHARS.numbers;
  if (useSpecial) pool += CHARS.special;
  if (!pool) return "";

  const arr = new Uint32Array(length);
  crypto.getRandomValues(arr);
  return Array.from(arr, (v) => pool[v % pool.length]).join("");
}

export default function Password() {
  const { translations } = useI18n();
  const t = translations.tools.passwordPage;

  const [length, setLength] = useState(16);
  const [useLetters, setUseLetters] = useState(true);
  const [useNumbers, setUseNumbers] = useState(true);
  const [useSpecial, setUseSpecial] = useState(false);
  const [mixedCase, setMixedCase] = useState(true);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  const canToggle = (checked: boolean, key: "letters" | "numbers" | "special") => {
    if (checked) return true;
    const others = { letters: useLetters, numbers: useNumbers, special: useSpecial };
    others[key] = true;
    return others.letters || others.numbers || others.special;
  };

  const handleToggle = (
    key: "letters" | "numbers" | "special",
    current: boolean,
    setter: (v: boolean) => void,
  ) => {
    if (current && !canToggle(false, key)) return;
    setter(!current);
  };

  const handleGenerate = useCallback(() => {
    setPassword(generatePassword(length, useLetters, useNumbers, useSpecial, mixedCase));
    setCopied(false);
  }, [length, useLetters, useNumbers, useSpecial, mixedCase]);

  const handleCopy = async () => {
    if (!password) return;
    await navigator.clipboard.writeText(password);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const sliderSteps = [8, 16, 24, 32, 40, 48, 56, 64];
  const stepIndex = sliderSteps.indexOf(length);
  const sliderMax = sliderSteps.length - 1;

  const handleSlider = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLength(sliderSteps[Number(e.target.value)]);
  };

  return (
    <Scroll>
      <main className="page">
        <Header
          title={t.title}
          breadcrumbs={[
            { label: "GAraujo", href: "/" },
            { label: translations.tools.nav, href: "/tools" },
            { label: t.title, href: "/tools/password" },
          ]}
        />
        <div className="content">
          <section className="pwd-tool">
            <div className="pwd-tool__controls">
              <div className="pwd-tool__slider-group">
                <div className="pwd-tool__slider-header">
                  <span className="pwd-tool__label">{t.lengthLabel}</span>
                  <span className="pwd-tool__value">{length}</span>
                </div>
                <input
                  className="pwd-tool__slider"
                  type="range"
                  min={0}
                  max={sliderMax}
                  step={1}
                  value={stepIndex}
                  onChange={handleSlider}
                />
                <div className="pwd-tool__slider-labels">
                  <span>{sliderSteps[0]}</span>
                  <span>{sliderSteps[sliderMax]}</span>
                </div>
              </div>

              <div className="pwd-tool__checkboxes">
                <label className="pwd-tool__check">
                  <input
                    type="checkbox"
                    checked={useLetters}
                    onChange={() => handleToggle("letters", useLetters, setUseLetters)}
                  />
                  <span>{t.letters}</span>
                </label>
                <label className="pwd-tool__check">
                  <input
                    type="checkbox"
                    checked={useNumbers}
                    onChange={() => handleToggle("numbers", useNumbers, setUseNumbers)}
                  />
                  <span>{t.numbers}</span>
                </label>
                <label className="pwd-tool__check">
                  <input
                    type="checkbox"
                    checked={useSpecial}
                    onChange={() => handleToggle("special", useSpecial, setUseSpecial)}
                  />
                  <span>{t.specialChars}</span>
                </label>
              </div>

              {useLetters && (
                <label className="pwd-tool__check">
                  <input
                    type="checkbox"
                    checked={mixedCase}
                    onChange={() => setMixedCase(!mixedCase)}
                  />
                  <span>{t.mixedCase}</span>
                </label>
              )}

              <button className="pwd-tool__generate" onClick={handleGenerate}>
                {t.generate}
              </button>
            </div>

            <div className="pwd-tool__result">
              {password ? (
                <>
                  <code className="pwd-tool__password">{password}</code>
                  <button
                    className="pwd-tool__copy"
                    onClick={handleCopy}
                  >
                    {copied ? t.copied : t.copy}
                  </button>
                </>
              ) : (
                <p className="pwd-tool__empty">{t.emptyMessage}</p>
              )}
            </div>
          </section>

          <AgentInfo
            toolUrl="/tools/password"
            toolName="generate_password"
            description={translations.tools.passwordDesc}
            steps={[
              translations.tools.passwordPage.agentStep1,
              translations.tools.passwordPage.agentStep2,
              translations.tools.passwordPage.agentStep3,
            ]}
          />
        </div>
        <Footer />
      </main>
    </Scroll>
  );
}
